import { z } from "zod";
/**
 * Malaysia phone number — E.164 normalised form, e.g. "+60123456789".
 * Accept input with or without country code; normalisation happens server-side.
 */
export const PhoneSchema = z
    .string()
    .trim()
    .min(8, "phone_too_short")
    .max(20, "phone_too_long")
    .regex(/^[+\d\s\-()]+$/, "phone_invalid");
export const CustomerRequestOtpSchema = z.object({
    phone: PhoneSchema,
});
export const CustomerVerifyOtpSchema = z.object({
    phone: PhoneSchema,
    code: z.string().length(6, "otp_length"),
});
export const StaffLoginSchema = z.object({
    staffId: PhoneSchema,
    pin: z.string().length(4, "pin_length"),
    // Declares which app is logging in (pos-app vs stall-app) so the backend can
    // reject a real, correctly-authenticated account that's just using the wrong
    // app, instead of silently issuing a token that then 403s on every route.
    expectedRole: z.enum(["pos_operator", "stall_staff"]).optional(),
});
export const AdminLoginSchema = z.object({
    email: z.string().email("email_invalid"),
    password: z.string().min(8, "password_too_short"),
    totpCode: z.string().length(6).optional(),
});
export const RefreshTokenSchema = z.object({
    refreshToken: z.string().min(10),
});
// --- Customer registration (email/phone choice) + password login ---------
const PasswordSchema = z.string().min(8, "password_too_short");
export const RegisterRequestOtpSchema = z.object({
    channel: z.enum(["phone", "email"]),
    identifier: z.string().trim().min(3),
});
export const RegisterVerifyOtpSchema = z.object({
    channel: z.enum(["phone", "email"]),
    identifier: z.string().trim().min(3),
    code: z.string().length(6, "otp_length"),
});
// For an email-channel registrationToken, the phone number must already be
// OTP-verified and baked into the token (see register/phone/verify-otp) —
// it's never accepted as a plain, unverified field here.
export const RegisterCompleteSchema = z.object({
    registrationToken: z.string().min(10),
    password: PasswordSchema,
    name: z.string().max(100).optional(),
});
// Second OTP leg for email-channel registration: verify ownership of the
// phone number being attached to the account before it's ever accepted.
export const RegisterPhoneRequestOtpSchema = z.object({
    registrationToken: z.string().min(10),
    phone: PhoneSchema,
});
export const RegisterPhoneVerifyOtpSchema = z.object({
    registrationToken: z.string().min(10),
    phone: PhoneSchema,
    code: z.string().length(6, "otp_length"),
});
export const CustomerPasswordLoginSchema = z.object({
    identifier: z.string().trim().min(3),
    password: z.string().min(1),
});
export const ForgotPasswordRequestOtpSchema = z.object({
    identifier: z.string().trim().min(3),
});
export const ForgotPasswordResetSchema = z.object({
    identifier: z.string().trim().min(3),
    code: z.string().length(6, "otp_length"),
    newPassword: PasswordSchema,
});
// `channel` lets an email-registered customer (with a verified phone) pick
// which identifier receives the OTP; omitted for accounts with only one
// usable channel. Validated server-side against what's actually usable for
// this customer, never trusted blindly.
export const ChangePasswordRequestOtpSchema = z.object({
    channel: z.enum(["phone", "email"]).optional(),
});
export const ChangePasswordResetSchema = z.object({
    code: z.string().length(6, "otp_length"),
    newPassword: PasswordSchema,
    channel: z.enum(["phone", "email"]).optional(),
});
//# sourceMappingURL=auth.js.map